import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { RootState } from '../../globalStore/store'
import { setDetails, setPackaging } from '../../reducers/product/productSlice';
import reqProvider from '../../util/requests/reqProvider';
import { useParams } from 'react-router-dom';
import { ProductVatData } from './productVatGrid';

interface CategoryOption {
  name: string;
  uuid: string;
}

interface NTAKCategory {
  id: number;
  name: string;
}

interface vatGroup {
  id: number;
  name: string;
}

const ProductDetails = ({ mode }: { mode: string }) => {
  const dispatch = useDispatch();
  const details = useSelector((state: RootState) => state.product.details);
  const { id } = useParams<{ id: string }>();
  const [NTAKMainCategories, setNTAKMainCategories] = React.useState<NTAKCategory[]>([]);
  const [NTAKSubCategories, setNTAKSubCategories] = React.useState<NTAKCategory[]>([]);
  const [vatGroups, setVatGroups] = useState<vatGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showVatHelper, setShowVatHelper] = useState(false);

  const [categories, setCategories] = useState({
    mainCategory: { name: '', options: [] as CategoryOption[] },
    subCategory: { name: '', options: [] as CategoryOption[] },
    subSubCategory: { name: '', options: [] as CategoryOption[] },
  });


  console.log(categories)

  useEffect(() => {
    reqProvider.get('/product/mainCategory')
      .then((response) => setNTAKMainCategories(response.data.content))
      .catch((error) => console.error('Error fetching main categories:', error));
  }, []);

  useEffect(() => {
    if (!details.mainCategoryId) return;
    reqProvider.get('/product/subCategory', {mainCategoryId: details.mainCategoryId})
      .then((response) => setNTAKSubCategories(response.data.content))
      .catch((error) => console.error('Error fetching sub categories:', error));
  }, [details.mainCategoryId]);

  useEffect(() => {
    reqProvider.get('product/custom-category', { posSetId: localStorage.getItem("productPosSetId") })
      .then(({ data }) => {
        setCategories((prev) => ({
          ...prev,
          mainCategory: { ...prev.mainCategory, options: data }
        }));
      })
      .catch(console.log);
  }, []);

  useEffect(() => {
    reqProvider.get('product/vatGroup')
      .then(response => {
        setVatGroups(response.data.content);
      })
      .catch(error => {
        console.error('Error fetching VAT groups:', error);
      });
  }, []);

  useEffect(() => {
    if (!categories.mainCategory.name) return;
    reqProvider.get('product/custom-category', {
      posSetId: localStorage.getItem("productPosSetId"),
      search: `${categories.mainCategory.name}/.`,
    })
      .then(({ data }) => {
        setCategories(prev => ({
          ...prev,
          subCategory: { ...prev.subCategory, options: data }
        }));
      })
      .catch(console.log);
  }, [categories.mainCategory.name]);

  useEffect(() => {
    if (!categories.mainCategory.name || !categories.subCategory.name) return;
    reqProvider.get('product/custom-category', {
      posSetId: localStorage.getItem("productPosSetId"),
      search: `${categories.mainCategory.name}/.${categories.subCategory.name}/.`,
    })
      .then(({ data }) => {
        setCategories(prev => ({
          ...prev,
          subSubCategory: { ...prev.subSubCategory, options: data }
        }));
      })
      .catch(console.log);
  }, [categories.mainCategory.name, categories.subCategory.name]);

  useEffect(() => {
    const { mainCategory, subCategory, subSubCategory } = categories;
    const categoryParts = [mainCategory.name, subCategory.name, subSubCategory.name].filter(Boolean);
    const customCategory = categoryParts.join('/.');
    dispatch(setDetails({ customCategory: customCategory === '' ? null : customCategory }));
  }, [categories]);

  useEffect(() => {
    if (mode === 'update' && id) {
      setIsLoading(true);
      reqProvider.get(`/product/${id}`)
        .then((response) => {
          const product = response.data;
          dispatch(setDetails({
            name: product.name,
            mainCategoryId: product.mainCategoryId,
            subCategoryId: product.subCategoryId,
            isTakeawayAllowed: product.isTakeawayAllowed,
            customCategory: product.customCategory,
            vatId: product.vatId,
            id: product.id,
            takeawayVatId: product.takeawayVatId,
            mainCategoryName: product.mainCategoryName,
            subCategoryName: product.subCategoryName,
          }));
          dispatch(setPackaging({
            units: product.units,
          }));
          if (product.customCategory) {
            const category = product.customCategory?.split('/.');
            setCategories(prev => ({
              mainCategory: { ...prev.mainCategory, name: category[0] ? category[0] : '' },
              subCategory: { ...prev.subCategory, name: category[1] ? category[1] : '' },
              subSubCategory: { ...prev.subSubCategory, name: category[2] ? category[2] : '' },
            }));
          }
        })
        .catch((error) => {
          console.error('Error fetching product details:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      dispatch(setDetails({
        name: '',
        mainCategoryId: null,
        subCategoryId: null,
        isTakeawayAllowed: false,
        customCategory: null,
        vatId: 12,
        id: null,
        takeawayVatId: null,
        mainCategoryName: '',
        subCategoryName: '',
      }));
      dispatch(setPackaging({
        units: [],
      }));
      setCategories({
        mainCategory: { name: '', options: [] },
        subCategory: { name: '', options: [] },
        subSubCategory: { name: '', options: [] },
      });
    }
  }, [id, mode]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    dispatch(setDetails({ [name]: value }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    dispatch(setDetails({ [name]: checked }));
  };

  const handleCategorySelect = (level: "mainCategory" | "subCategory" | "subSubCategory", value) => {
    setCategories(prev => {
      if (level === "mainCategory") {
        return {
          mainCategory: { ...prev.mainCategory, name: value.name },
          subCategory: { name: '', options: [] },
          subSubCategory: { name: '', options: [] }
        }
      }
      if (level === "subCategory") {
        return {
          ...prev,
          subCategory: { ...prev.subCategory, name: value.name },
          subSubCategory: { name: '', options: [] }
        }
      }
      return {
        ...prev,
        subSubCategory: { ...prev.subSubCategory, name: value.name }
      }
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '25rem'}}>
      {isLoading ?
        <CircularProgress />
        :
        <>
        <TextField
        label="Termék neve"
        name="name"
        value={details.name}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
      />
      <Autocomplete
        options={NTAKMainCategories}
        value={NTAKMainCategories.find(elem => elem.id === details.mainCategoryId) || null}
        onChange={(_, newValue) => dispatch(setDetails({ mainCategoryId: newValue ? newValue.id : null, subCategoryId: null }))}
        getOptionLabel={(option) => option.name || ''}
        renderInput={(params) => (
          <TextField
            {...params}
            label="NTAK Főkategória"
            fullWidth
            margin="normal"
          />
        )}
      />
      {details.mainCategoryId && (
        <Autocomplete
          options={NTAKSubCategories}
          value={NTAKSubCategories.find(elem => elem.id === details.subCategoryId) || null}
          onChange={(_, newValue) => dispatch(setDetails({ subCategoryId: newValue ? newValue.id : null }))}
          getOptionLabel={(option) => option.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label="NTAK Alkategória"
              fullWidth
              margin="normal"
            />
          )}
        />
      )}
          <Autocomplete
            options={categories.mainCategory.options}
            getOptionLabel={(option: string | CategoryOption) => typeof option === 'string' ? option : (option.name || '')}
            value={categories.mainCategory.options.find(opt => opt.name === categories.mainCategory.name) || null}
            onChange={(_, newValue) =>
              handleCategorySelect('mainCategory', typeof newValue === 'string' ? {
                name: newValue,
                uuid: '',
              } : newValue || { name: '', uuid: '' })}
            freeSolo
            onInputChange={(_, value) => {
              if (!categories.mainCategory.options.find(opt => opt.name === value)) {
                handleCategorySelect('mainCategory', { name: value, uuid: '' });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gyűjtőkategória"
                fullWidth
                margin="normal"
              />
            )}
          />
      {categories.mainCategory.name && (
        <Autocomplete
          options={categories.subCategory.options}
          getOptionLabel={(option: string | CategoryOption) => typeof option === 'string' ? option : (option.name || '')}
          value={categories.subCategory.options.find(opt => opt.name === categories.subCategory.name) || null}
          onChange={(_, newValue) =>
            handleCategorySelect('subCategory', typeof newValue === 'string' ? {
              name: newValue,
              uuid: '',
            } : newValue || { name: '', uuid: '' })}
          freeSolo
          onInputChange={(_, value) => {
            if (!categories.subCategory.options.find(opt => opt.name === value)) {
              handleCategorySelect('subCategory', { name: value, uuid: '' });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Főkategória"
              fullWidth
              margin="normal"
            />
          )}
        />
      )}
      {categories.subCategory.name && (
        <Autocomplete
          options={categories.subSubCategory.options}
          getOptionLabel={(option: string | CategoryOption) => typeof option === 'string' ? option : (option.name || '')}
          value={categories.subSubCategory.options.find(opt => opt.name === categories.subSubCategory.name) || null}
          onChange={(_, newValue) =>
            handleCategorySelect('subSubCategory', typeof newValue === 'string' ? {
              name: newValue,
              uuid: '',
            } : newValue || { name: '', uuid: '' })}
          freeSolo
          onInputChange={(_, value) => {
            if (!categories.subSubCategory.options.find(opt => opt.name === value)) {
              handleCategorySelect('subSubCategory', { name: value, uuid: '' });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Alkategória"
              fullWidth
              margin="normal"
            />
          )}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: '100%' }}>
        <Autocomplete
          sx={{ width: { xs: '100%', sm: '45%' } }}
          options={vatGroups}
          getOptionLabel={(option: string | vatGroup) => typeof option === 'string' ? option : (option.name || '')}
          value={vatGroups.find(opt => opt.id === details.vatId) || null}
          onChange={(_, newValue) =>
            dispatch(setDetails({ vatId: newValue ? newValue.id : null }))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="ÁFA csoport"
              fullWidth
              margin="normal"
            />
          )}
        />
        <Button
          sx={{ width: { xs: '100%', sm: '45%' }, textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' } }}
          variant="text"
          onClick={() => setShowVatHelper(true)}
        >
          ÁFA segédlet mutatása
        </Button>
        <Dialog
          open={showVatHelper}
          onClose={() => setShowVatHelper(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>ÁFA segédlet</DialogTitle>
          <DialogContent>
            <DataGrid
              rows={ProductVatData}
              columns={[
                { field: 'name', headerName: 'Név', width: 500 },
                { field: 'localVat', headerName: 'Helyi ÁFA', width: 120, sortable: false },
                { field: 'takeawayVat', headerName: 'Elviteles ÁFA', width: 120, sortable: false },
                { field: 'deliveryVat', headerName: 'Kiszállítás ÁFA', width: 120, sortable: false },
              ]}
              autoHeight
              hideFooter
              disableColumnMenu
            />
          </DialogContent>
        </Dialog>
      </Box>
      <FormControlLabel
        sx={{ alignSelf: 'flex-start' }}
        control={
          <Checkbox
            name="isTakeawayAllowed"
            checked={details.isTakeawayAllowed}
            onChange={handleCheckboxChange}
          />
        }
        label="Elvihető"
      />
          {details.isTakeawayAllowed &&
            <Autocomplete
              options={vatGroups}
              getOptionLabel={(option: string | vatGroup) => typeof option === 'string' ? option : (option.name || '')}
              value={vatGroups.find(opt => opt.id === details.takeawayVatId) || null}
              onChange={(_, newValue) =>
                dispatch(setDetails({ takeawayVatId: newValue ? newValue.id : null }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Elviteles ÁFA csoport"
                  fullWidth
                  margin="normal"
                />
              )}
            />
          }
      </>}
    </Box>
  );
};

export default ProductDetails;
